"use client";

import { AnimatePresence, motion } from "motion/react";
import * as React from "react";
import { cn } from "@/lib/utils";

const MotionHighlightContext = React.createContext(undefined);

function useMotionHighlight() {
  const context = React.useContext(MotionHighlightContext);
  if (!context) {
    throw new Error("useMotionHighlight must be used within MotionHighlight");
  }
  return context;
}

const MotionHighlight = React.forwardRef(function MotionHighlight(
  {
    children,
    value,
    defaultValue = null,
    onValueChange,
    className,
    activeClassName,
    transition = { type: "spring", stiffness: 350, damping: 35 },
    hover = true,
    enabled = true,
    disabled = false,
    exitDelay = 0.15,
    controlledItems = false,
    itemsClassName,
    ...props
  },
  ref
) {
  const localRef = React.useRef(null);
  React.useImperativeHandle(ref, () => localRef.current);

  const [activeValue, setActiveValue] = React.useState(value ?? defaultValue);
  const id = React.useId();

  React.useEffect(() => {
    if (value !== undefined) setActiveValue(value);
  }, [value]);

  const safeSetActiveValue = React.useCallback(
    (next) => {
      setActiveValue((prev) => (prev === next ? prev : next));
      if (next !== activeValue) onValueChange?.(next);
    },
    [activeValue, onValueChange]
  );

  return (
    <MotionHighlightContext.Provider
      value={{
        activeValue,
        setActiveValue: safeSetActiveValue,
        hover,
        className,
        activeClassName,
        transition,
        disabled,
        enabled,
        exitDelay,
        contextId: id,
      }}
    >
      <div ref={localRef} {...props}>
        {enabled
          ? controlledItems
            ? children
            : React.Children.map(children, (child, index) => (
                <MotionHighlightItem className={itemsClassName} key={index} asChild>
                  {child}
                </MotionHighlightItem>
              ))
          : children}
      </div>
    </MotionHighlightContext.Provider>
  );
});

const MotionHighlightItem = React.forwardRef(function MotionHighlightItem(
  {
    children,
    id,
    value,
    className,
    transition,
    activeClassName,
    disabled = false,
    exitDelay,
    asChild = false,
    ...props
  },
  ref
) {
  const {
    activeValue,
    setActiveValue,
    hover,
    className: contextClassName,
    activeClassName: contextActiveClassName,
    transition: contextTransition,
    disabled: contextDisabled,
    exitDelay: contextExitDelay,
    contextId,
  } = useMotionHighlight();

  const localRef = React.useRef(null);
  React.useImperativeHandle(ref, () => localRef.current);
  const itemId = React.useId();
  const childValue = id ?? value ?? children?.props?.id ?? itemId;
  const isActive = activeValue === childValue;
  const isDisabled = disabled ?? contextDisabled;
  const itemTransition = transition ?? contextTransition;
  const resolvedActiveClassName = activeClassName ?? contextActiveClassName;

  const handlers = hover
    ? {
        onMouseEnter: (e) => {
          setActiveValue(childValue);
          children?.props?.onMouseEnter?.(e);
        },
        onMouseLeave: (e) => {
          setActiveValue(null);
          children?.props?.onMouseLeave?.(e);
        },
      }
    : {
        onClick: (e) => {
          setActiveValue(childValue);
          children?.props?.onClick?.(e);
        },
      };

  if (!React.isValidElement(children)) return children;

  if (asChild) {
    return React.cloneElement(
      children,
      {
        ref: localRef,
        className: cn("relative z-[1]", children.props.className),
        ...handlers,
        ...props,
      },
      <>
        <AnimatePresence initial={false}>
          {isActive && !isDisabled && (
            <motion.div
              layoutId={`transition-background-${contextId}`}
              className={cn("absolute inset-0 z-0 bg-muted", contextClassName, resolvedActiveClassName)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: {
                  ...itemTransition,
                  delay: (itemTransition?.delay ?? 0) + (exitDelay ?? contextExitDelay ?? 0),
                },
              }}
              transition={itemTransition}
            />
          )}
        </AnimatePresence>
        <span className={cn("relative z-[1] inline-flex w-full h-full items-center justify-center", className)}>
          {children.props.children}
        </span>
      </>
    );
  }

  return (
    <div ref={localRef} className={cn("relative", className)} {...handlers} {...props}>
      <AnimatePresence initial={false}>
        {isActive && !isDisabled && (
          <motion.div
            layoutId={`transition-background-${contextId}`}
            className={cn("absolute inset-0 z-0 bg-muted", contextClassName, resolvedActiveClassName)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={itemTransition}
          />
        )}
      </AnimatePresence>
      <div className="relative z-[1]">{children}</div>
    </div>
  );
});

export { MotionHighlight, MotionHighlightItem, useMotionHighlight };
export default MotionHighlight;
